import React, { useCallback, useMemo, useState } from 'react';
import VehicleDisplay from './VehicleDisplay';
import { CoverageVehicleFields, generateDeductibleOptions, generateTowingAndLaborOptions } from './VehicleFields';
import HelpButton from '../HelpButton';
import Form from '../FormDisplay';
import styles from './VehicleCoverageSelection.module.scss';
import { Button, Segment, Header, Table, Modal, Loader, Icon } from 'semantic-ui-react';
import { useMapToOptions } from '../utilities';
import PolicyRate from '../../PolicyRate';
import VinModal from './VinModal';
import ConfirmationModal from '../ConfirmationModal';
import moment from 'moment';
import {newCoEffectiveDate} from './../../../../../helpers/date';

const VehicleCoverageSelection = ({ data, previousVehicle, onChange, onRate, onRateConfirm, onCancel, onSaveAndExit, rate, policy, onBack, vehicleState, success, onExit, currentEnhancement, actions,effectiveDate}) => {
	const comprehensiveOptions = useMemo(() => generateDeductibleOptions(CoverageVehicleFields.comprehensive.options, 'No Coverage'), []);
	const collisionOptions = useMemo(() => generateDeductibleOptions(CoverageVehicleFields.collision.options, 'No Coverage'), []);
	const rentalReimbursementOptions = useMapToOptions(CoverageVehicleFields.rentalReimbursement.options, false, '');
	const rentalReimbursementNewco = useMapToOptions(CoverageVehicleFields.rentalReimbursementNewco.options,false,'');
	const towingAndLaborOptions = useMemo(() => generateTowingAndLaborOptions(CoverageVehicleFields.towingAndLabor.options, 'No Coverage'), []);

	const [hasChanged, setHasChanged] = useState(false);

	const _onChange = useCallback(
		(_, { name, value }) => {
			const changes = { [name]: value };

			if (name === 'comprehensive' && (value === null || value === 0)) {
				changes.collision = 0;
				changes.rentalReimbursement = 0;
				changes.towingAndLabor = 0;
			}

			onChange(changes);
			setHasChanged(true);
		},
		[onChange]
	);

	const isCalculated = useMemo(() => !hasChanged && rate, [hasChanged, rate]);

	const onEnhancementChange = useCallback(
		(change) => {
			onChange(change);
			setHasChanged(true);
		},
		[onChange]
	);

	const [showWarning, setShowWarning] = useState(false);

	const getRate = useCallback(() => {
		onRate();
		setHasChanged(false);
	}, [onRate]);

	const _onRate = useCallback(async () => {
		if (!data.comprehensive) {
			setShowWarning(true);
		} else {
			getRate();
		}
	}, [data.comprehensive, getRate]);

	const hasBetter = useMemo(() => currentEnhancement === 'better' || currentEnhancement === 'best', [currentEnhancement]);
	const hasBest = useMemo(() => currentEnhancement === 'best', [currentEnhancement]);

	const [showVinModal, setShowVinModal] = useState(false);

	const hasVin = useMemo(() => data.vin && data.vin !== '', [data.vin]);

	const onVinContinue = useCallback(
		async (_vehicleState) => {
			setShowVinModal(false);
			onRate(_vehicleState);
			setHasChanged(false);
		},
		[onRate]
	);

	const hasComprehensive = useMemo(() => data.comprehensive > 0, [data.comprehensive]);
	const isbefore = moment(policy.term.effectiveDate).isBefore(newCoEffectiveDate);
	const showLoanLeaseCoverage = useMemo(() => data.loanOrLease && !data.hasThirdPartyLoanLeaseCoverage && data.hasThirdPartyLoanLeaseCoverage !== null, [data.hasThirdPartyLoanLeaseCoverage, data.loanOrLease]);
	const showBundles = useMemo(() => !hasBetter || !hasBest, [hasBest, hasBetter]);

	return (
		<React.Fragment>
			<Modal open={showWarning} onClose={() => setShowWarning(false)} size='tiny'>
				<Modal.Header>Warning</Modal.Header>
				<Modal.Content>You’re adding this vehicle with liability coverage only. Please hit YES to continue or NO if you wish to add coverage.</Modal.Content>
				<Modal.Actions>
					<Button id='noBtn' primary onClick={() => setShowWarning(false)}>
						No
					</Button>
					<Button
						id='yesBtn'
						primary
						onClick={() => {
							setShowWarning(false);
							getRate();
						}}>
						Yes
					</Button>
				</Modal.Actions>
			</Modal>

			<VinModal open={showVinModal} onClose={() => setShowVinModal(false)} onContinue={onVinContinue} actions={actions} versionId={policy.versionId} />

			{success && <Header className={`${styles.center} ${styles.green}`}>Your vehicle and coverage information was saved successfully!</Header>}

			<Form>
				{!success && !data.addBy && (
					<Segment className={styles.warning}>
						<div>The change in premium is just an estimate based on the selections you have made.</div>
						<div>A valid VIN will be required to complete your change.</div>
					</Segment>
				)}
				<Table>
					<Table.Header>
						<Table.Row>
							<Table.HeaderCell></Table.HeaderCell>
							<Table.HeaderCell>Policy Vehicle Coverage</Table.HeaderCell>
							{previousVehicle && <Table.HeaderCell>Previous Policy Vehicle Coverage</Table.HeaderCell>}
						</Table.Row>
					</Table.Header>
					<Table.Body>
						<Table.Row>
							<Table.Cell></Table.Cell>
							<Table.Cell>
								{vehicleState.vehicle && <VehicleDisplay vehicle={vehicleState.vehicle} showVin />}

								{vehicleState.loading && <Loader active inline />}
							</Table.Cell>
							{previousVehicle && (
								<Table.Cell>
									<VehicleDisplay vehicle={previousVehicle} showVin />
								</Table.Cell>
							)}
						</Table.Row>
						<Table.Row>
							<Table.Cell textAlign='right'>
								<label>
									{CoverageVehicleFields.comprehensive.label}
									<HelpButton>{CoverageVehicleFields.comprehensive.help}</HelpButton>
								</label>
							</Table.Cell>
							<Table.Cell>
								<Form.Dropdown id='comprehensive' name='comprehensive' options={comprehensiveOptions} value={data.comprehensive} onChange={_onChange} selection disabled={success} />
							</Table.Cell>
							{previousVehicle && (
								<Table.Cell>
									<Form.Display value={(comprehensiveOptions.find((c) => c.value === previousVehicle.comprehensive) || {}).text} />
								</Table.Cell>
							)}
						</Table.Row>
						<Table.Row>
							<Table.Cell textAlign='right'>
								<label>
									{CoverageVehicleFields.collision.label}
									<HelpButton>{CoverageVehicleFields.collision.help}</HelpButton>
								</label>
							</Table.Cell>
							<Table.Cell>
								<Form.Dropdown id='collision' name='collision' options={collisionOptions} value={data.collision} onChange={_onChange} selection disabled={success || !hasComprehensive} />
							</Table.Cell>
							{previousVehicle && (
								<Table.Cell>
									<Form.Display value={collisionOptions.find((c) => c.value === previousVehicle.collision).text} />
								</Table.Cell>
							)}
						</Table.Row>
						<Table.Row>
							<Table.Cell textAlign='right'>
								<label>
									{CoverageVehicleFields.rentalReimbursement.label}
									<HelpButton>{CoverageVehicleFields.rentalReimbursement.help}</HelpButton>
								</label>
							</Table.Cell>
							<Table.Cell>
								{hasComprehensive && isbefore && <Form.Dropdown id='rentalReimbursement' name='rentalReimbursement' options={rentalReimbursementOptions} value={data.rentalReimbursement} onChange={_onChange} selection disabled={success} />}
								{hasComprehensive && !isbefore &&<Form.Dropdown id='rentalReimbursement' name='rentalReimbursement' options={rentalReimbursementNewco} value={data.rentalReimbursement} onChange={_onChange} selection disabled={success} />}
								{!hasComprehensive && <Form.Dropdown id='rentalReimbursement' name='rentalReimbursement' options={collisionOptions} value={0} selection disabled />}
							</Table.Cell>
							{previousVehicle && (
								<Table.Cell>
									<Form.Display value={rentalReimbursementOptions.find((o) => o.value === previousVehicle.rentalReimbursement).text} />
								</Table.Cell>
							)}
						</Table.Row>
						<Table.Row>
							<Table.Cell textAlign='right'>
								<label>
									{CoverageVehicleFields.towingAndLabor.label}
									<HelpButton>{CoverageVehicleFields.towingAndLabor.help}</HelpButton>
								</label>
							</Table.Cell>
							<Table.Cell>
								<Form.Dropdown id='towingAndLabor' name='towingAndLabor' options={towingAndLaborOptions} value={data.towingAndLabor} onChange={_onChange} selection disabled={success || !hasComprehensive} />
							</Table.Cell>
							{previousVehicle && (
								<Table.Cell>
									<Form.Display value={towingAndLaborOptions.find((o) => o.value === previousVehicle.towingAndLabor).text} />
								</Table.Cell>
							)}
						</Table.Row>
					</Table.Body>
				</Table>

				{(showBundles || showLoanLeaseCoverage) && (
					<Segment>
						<Header>Additional Coverage</Header>
						{!hasBetter && <Form.Checkbox id='betterBundle' label='Make it better bundle - $35 a year for all covered vehicles' checked={data.enhancement === 'better'} onClick={(_, { checked }) => onEnhancementChange({ enhancement: checked ? 'better' : currentEnhancement })} disabled={success} />}
						{!hasBest && <Form.Checkbox id='evenBetterBundle' label={`Make it even better bundle - $${hasBetter ? 30 : 65} a year for all covered vehicles`} checked={data.enhancement === 'best'} onClick={(_, { checked }) => onEnhancementChange({ enhancement: checked ? 'best' : currentEnhancement })} disabled={success} />}
						{showLoanLeaseCoverage && <Form.Checkbox id='loanLeaseCoverage' label='Loan/Lease Coverage' checked={data.addLoanLeaseCoverage} onClick={(_, { checked }) => onEnhancementChange({ addLoanLeaseCoverage: checked })} disabled={success} />}
					</Segment>
				)}

				<PolicyRate rate={rate} policy={policy} />
				{!success && (
					<div className={styles.buttons}>
						<Button id='calculateBtn' disabled={!hasChanged && !!rate} onClick={_onRate} primary>
							{rate ? 'Recalculate' : 'Calculate'}
						</Button>
						{hasVin && previousVehicle && (
							<ConfirmationModal onContinue={onRateConfirm}>
								<Button id='completeChangeBtn' disabled={!isCalculated} primary>
									Complete Change
								</Button>
							</ConfirmationModal>
						)}

						{hasVin && !previousVehicle && (
							<Button id='completeChangeBtn' disabled={!isCalculated} primary onClick={onRateConfirm}>
								Complete Change
							</Button>
						)}

						{!hasVin && (
							<Button id='completeChangeBtn' disabled={!isCalculated} onClick={() => setShowVinModal(true)} primary>
								Complete Change
							</Button>
						)}

						<Button id='saveAndExitBtn' onClick={onSaveAndExit} primary>
							Save and Exit
						</Button>
						<Button id='backBtn' onClick={onBack} primary>
							Back
						</Button>
						<Button id='cancelBtn' onClick={onCancel} primary>
							Cancel
						</Button>
					</div>
				)}
			</Form>
			{success && <Header className={styles.center}>You will receive a confirmation email of this information shortly.</Header>}
			{success && (
				<div className={styles.buttons}>
					<Button id='closeBtn' primary onClick={onExit}>
						Close
					</Button>
					<Button id='printBtn' icon primary onClick={() => window.print()}>
						<Icon name='print' />
						Print
					</Button>
				</div>
			)}
		</React.Fragment>
	);
};

export default VehicleCoverageSelection;
